import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type ThemePref = "system" | "light" | "dark";
export type Theme = "light" | "dark";

/** Same key as the inline script in index.html, which applies the theme before React loads (no flash). */
const KEY = "ichegou.admin.theme";
const media = () => window.matchMedia("(prefers-color-scheme: dark)");

function readPref(): ThemePref {
  try {
    const v = localStorage.getItem(KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch {}
  return "system";
}

type Ctx = { pref: ThemePref; theme: Theme; setPref: (p: ThemePref) => void };
const ThemeContext = createContext<Ctx | null>(null);

/** Light / dark / system for the whole admin, remembered per browser; sets `data-theme` on <html>. */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [pref, setPrefState] = useState<ThemePref>(readPref);
  const [systemDark, setSystemDark] = useState(() => media().matches);
  useEffect(() => {
    const m = media();
    const on = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  const theme: Theme = pref === "system" ? (systemDark ? "dark" : "light") : pref;
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", PALETTE[theme].frame);
  }, [theme]);
  const setPref = (p: ThemePref) => {
    setPrefState(p);
    try {
      localStorage.setItem(KEY, p);
    } catch {}
  };
  return <ThemeContext.Provider value={{ pref, theme, setPref }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme outside ThemeProvider");
  return ctx;
}

/**
 * Colours for things CSS variables cannot reach: chart marks, map markers, canvas dots and Google
 * Maps styles. Keep in step with the tokens in styles.css.
 */
export const PALETTE = {
  light: {
    frame: "#E4DED7", card: "#FFFFFF", ink: "#15110E", inkSoft: "#36302B", muted: "#8C8278", neutral: "#B3AAA0",
    brasa: "#FF6B1A", brasaSoft: "#FFC59E", ok: "#1F9D55", warn: "#E3A008", bad: "#D92D20", info: "#2563EB",
    /** Map: marker outline (contrast with tiles) and the three trip states. */
    ring: "#FFFFFF",
    trip: { fill: "#15110E", arrow: "#FF8436" },
    pickup: { fill: "#FFFFFF", arrow: "#15110E" },
    searching: { fill: "#B3AAA0", arrow: "#FFFFFF" },
    driver: { free: "#1F9D55", offered: "#E3A008", pickup: "#36302B", trip: "#FF6B1A" },
  },
  dark: {
    frame: "#080706", card: "#1E1A17", ink: "#FAF8F6", inkSoft: "#E7E2DC", muted: "#8C8278", neutral: "#6B6259",
    brasa: "#FF8436", brasaSoft: "#9A3412", ok: "#2BD576", warn: "#F5C04A", bad: "#F97066", info: "#7DA2FF",
    ring: "#120F0D",
    trip: { fill: "#231F1C", arrow: "#FF8436" },
    pickup: { fill: "#FAF8F6", arrow: "#15110E" },
    searching: { fill: "#36302B", arrow: "#B3AAA0" },
    driver: { free: "#2BD576", offered: "#F5C04A", pickup: "#FAF8F6", trip: "#FF8436" },
  },
} as const;

export type Palette = (typeof PALETTE)[Theme];
export const usePalette = (): Palette => PALETTE[useTheme().theme];
